import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import CurrenciesApi from 'api/setting/finances/currency.api'

/** STYLE */
import useStyles from 'features/setting/finances/currencies/style'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import InputCheckbox from 'component/Input/InputCheckbox'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  CURRENCY_CODE: ``,
  CURRENCY_DISPLAY: ``,
  CURRENCY_NAME: ``,
  CURRENCY_NAME_EN: ``,
  CURRENCY_FIX_RATIO: ``,
  CURRENCY_RATIO: ``,
  CURRENCY_SORTING: ``,

  NOT_FOUND_CURRENCY: ``,
  CURRENCY_ALREADY_SYSTEM: ``,
  INVALID_CREATED: ``,
  INVALID_UPDATED: ``,
  IS_DUPLICATE_CURRENCY_CODE: ``,
  IS_DUPLICATE_CURRENCY_NAME: ``,
  IS_DUPLICATE_CURRENCY_NAME_EN: ``,

  CURRENCY_CODE_STRING_BASE: ``,
  CURRENCY_CODE_STRING_EMPTY: ``,
  CURRENCY_CODE_MAXIMUM_LENGTH: ``,
  CURRENCY_CODE_ANY_REQUIRED: ``,

  CURRENCY_DISPLAY_STRING_BASE: ``,
  CURRENCY_DISPLAY_STRING_EMPTY: ``,
  CURRENCY_DISPLAY_MAXIMUM_LENGTH: ``,
  CURRENCY_DISPLAY_ANY_REQUIRED: ``,

  CURRENCY_NAME_STRING_BASE: ``,
  CURRENCY_NAME_STRING_EMPTY: ``,
  CURRENCY_NAME_MAXIMUM_LENGTH: ``,
  CURRENCY_NAME_ANY_REQUIRED: ``,

  CURRENCY_NAME_EN_STRING_BASE: ``,
  CURRENCY_NAME_EN_STRING_EMPTY: ``,
  CURRENCY_NAME_EN_MAXIMUM_LENGTH: ``,
  CURRENCY_NAME_EN_ANY_REQUIRED: ``
}
export default function Currencies() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [currencys, setCurrencys] = useState([])

  const [currencyId, setCurrencyId] = useState(Number)
  const [currencyCode, setCurrencyCode] = useState('')
  const [currencyDisplay, setCurrencyDisplay] = useState('')
  const [currencyName, setCurrencyName] = useState('')
  const [currencyNameEn, setCurrencyNameEn] = useState('')
  const [currencyFixRatio, setCurrencyFixRatio] = useState('NOT_FIXED')
  const [currencyRatio, setCurrencyRatio] = useState(Number)
  const [currencySorting, setCurrencySorting] = useState(Number)
  const [currencyStatus, setCurrencyStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await CurrenciesApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setCurrencys(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {

    if (!currencyCode || !currencyDisplay || !currencyName || !currencyName || !currencyFixRatio || (!currencyRatio && currencyRatio !== 0)) {
      return setErrorMessage({
        ...errorMessage,
        ...{
          CURRENCY_CODE: !currencyCode ? t(`CURRENCY.MESSAGE.CURRENCY_CODE`) : '',
          CURRENCY_DISPLAY: !currencyDisplay ? t(`CURRENCY.MESSAGE.CURRENCY_DISPLAY`) : '',
          CURRENCY_NAME: !currencyName ? t(`CURRENCY.MESSAGE.CURRENCY_NAME`) : '',
          CURRENCY_FIX_RATIO: !currencyFixRatio ? t(`CURRENCY.MESSAGE.CURRENCY_FIX_RATIO`) : '',
          CURRENCY_RATIO: !currencyRatio && currencyRatio !== 0 ? t(`CURRENCY.MESSAGE.CURRENCY_RATIO`) : ''
        }
      })
    }

    dispatch(submitModal())
    CurrenciesApi.create({ currencyCode, currencyDisplay, currencyName, currencyNameEn, fixRatio: currencyFixRatio, ratio: currencyRatio, sorting: currencySorting })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t(`CURRENCY.MESSAGE.SUCCESS.CREATE`), '', null, null)
          resetForm()

          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data

          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CURRENCY.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CURRENCY.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {

    if (!currencyCode || !currencyDisplay || !currencyName || !currencyName || !currencyFixRatio || (!currencyRatio && currencyRatio !== 0)) {
      return setErrorMessage({
        ...errorMessage,
        ...{
          CURRENCY_CODE: !currencyCode ? t(`CURRENCY.MESSAGE.CURRENCY_CODE`) : '',
          CURRENCY_DISPLAY: !currencyDisplay ? t(`CURRENCY.MESSAGE.CURRENCY_DISPLAY`) : '',
          CURRENCY_NAME: !currencyName ? t(`CURRENCY.MESSAGE.CURRENCY_NAME`) : '',
          CURRENCY_FIX_RATIO: !currencyFixRatio ? t(`CURRENCY.MESSAGE.CURRENCY_FIX_RATIO`) : '',
          CURRENCY_RATIO: !currencyRatio && currencyRatio !== 0 ? t(`CURRENCY.MESSAGE.CURRENCY_RATIO`) : ''
        }
      })
    }

    dispatch(submitModal())
    CurrenciesApi.update(currencyId, { currencyCode, currencyDisplay, currencyName, currencyNameEn, fixRatio: currencyFixRatio, ratio: currencyRatio, sorting: currencySorting, status: currencyStatus })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`CURRENCY.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CURRENCY.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`CURRENCY.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setCurrencyId(0)
    setCurrencyCode('')
    setCurrencyDisplay('')
    setCurrencyName('')
    setCurrencyFixRatio('NOT_FIXED')
    setCurrencyRatio(0)
    setCurrencySorting(0)
    setCurrencyStatus('')
    clearErrorMessage()
  }
  const onActive = (id: number, valueStatus: string) => {
    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('CURRENCY.ALERT.CONFIRM_STATUS')}</p>`,
      null, (res: any) => {
        if (res) {
          CurrenciesApi.updateStatus(id, valueStatus)
            .then((resp) => {
              notiSuccess(t('CURRENCY.MESSAGE.SUCCESS.UPDATE_STATUS'))
              loadData()
            })
            .catch((e) => {
              notiError(t('CURRENCY.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL'))
  }
  const onDelete = (data: any) => {
    const { currencyId: crrId, currencyName: crrName } = data

    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('CURRENCY.ALERT.CONFIRM_DELETE')}</p>`,
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('CURRENCY.ALERT.CONFIRM_DELETE_', {
        currencyName: crrName
      })}</p>`,
      (res: any) => {
        if (res) {
          CurrenciesApi.remove(crrId)
            .then((resp) => {
              notiSuccess(t('CURRENCY.MESSAGE.SUCCESS.DELETE'))
              loadData()
            })
            .catch((e) => {
              notiError(t('CURRENCY.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
    )
  }

  const onEdit = (data: any) => {
    const { currencyId: crrId, currencyCode: crrCode, currencyDisplay: crrDisplay, currencyName: crrName, fixRatio, ratio, status: crrStatus } = data

    setCurrencyId(crrId)
    setCurrencyCode(crrCode)
    setCurrencyDisplay(crrDisplay)
    setCurrencyName(crrName)
    setCurrencyFixRatio(fixRatio)
    setCurrencyRatio(ratio)
    setCurrencyStatus(crrStatus)
    dispatch(showModal())
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'currencyCode', disablePadding: false, label: t('CURRENCY.TABLE.CELL.CURRENCY_CODE') },
    { id: 'currencyDisplay', disablePadding: false, label: t('CURRENCY.TABLE.CELL.CURRENCY_DISPLAY') },
    { id: 'currencyName', disablePadding: false, label: t('CURRENCY.TABLE.CELL.CURRENCY_NAME') },
    { id: 'currencyNameEn', disablePadding: false, label: t('CURRENCY.TABLE.CELL.CURRENCY_NAME_EN') },
    { id: 'currencyRatio', disablePadding: false, label: t('CURRENCY.TABLE.CELL.CURRENCY_RATIO') },
    { id: 'status', disablePadding: false, label: t('CURRENCY.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('CURRENCY.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { currencyId: crrId, currencyCode: crrCode, currencyDisplay: crrDisplay, currencyName: crrName, currencyNameEn: crrNameEn, ratio, updatedBy, updatedAt, isDefault, status: crrStatus } = objData
    const statusBtnActive = crrStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = crrStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: crrId,
      id: crrId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: crrCode },
        { option: 'TEXT', align: 'left', label: crrDisplay },
        { option: 'TEXT', align: 'left', label: crrName },
        { option: 'TEXT', align: 'left', label: crrNameEn },
        { option: 'TEXT', align: 'left', label: ratio },
        { option: 'STATUS', align: 'center', label: crrStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        isDefault !== `1` && {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => onActive(Number(objRenderData.id), 'ACTIVE')}
        oninactive={() => onActive(Number(objRenderData.id), 'INACTIVE')}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => onDelete(objRenderData.obj)}
      />
    )
  }

  return (
    <div className={classes.root}>
      <Card className="border-0 h-100">
        <HeaderCard text={t('CURRENCY.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('INPUT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => dispatch(showModal())} textButton={t('CURRENCY.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={currencys.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <ModalCustom
        modalStyle={classes.modal}
        title={currencyId ? t('CURRENCY.TITLE_UPDATE') : t('CURRENCY.TITLE_CREATE')}
        component={
          <div className="pb-2">
            {currencyId ? (
              <div>
                <Form className="mx-0 mt-0 mb-3 p-0  d-inline-flex w-100">
                  <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
                  <Form.Check type="switch" id="custom-switch" onChange={() => setCurrencyStatus(currencyStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={currencyStatus === 'ACTIVE' ? true : false} style={{ marginLeft: 'auto' }} />
                </Form>
              </div>
            ) : (
              ''
            )}

            <Row>
              <Col sm={12} md={6} style={{ paddingRight: '8px' }}>
                <InputTextField
                  onchange={(event) => {
                    setCurrencyCode(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 3 }}
                  value={currencyCode}
                  label={t('CURRENCY.INPUT.CURRENCY_CODE')}
                  helperText={
                    errorMessage.CURRENCY_CODE ||
                    errorMessage.IS_DUPLICATE_CURRENCY_CODE ||
                    errorMessage.CURRENCY_CODE_STRING_BASE ||
                    errorMessage.CURRENCY_CODE_STRING_EMPTY ||
                    errorMessage.CURRENCY_CODE_MAXIMUM_LENGTH ||
                    errorMessage.CURRENCY_CODE_ANY_REQUIRED
                  }
                  required={true}
                  style={{ margin: '1rem 0' }}
                />
              </Col>
              <Col sm={12} md={6} style={{ paddingLeft: '8px' }}>
                <InputTextField
                  onchange={(event) => {
                    setCurrencyDisplay(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 10 }}
                  value={currencyDisplay}
                  label={t('CURRENCY.INPUT.CURRENCY_DISPLAY')}
                  helperText={errorMessage.CURRENCY_DISPLAY || errorMessage.CURRENCY_DISPLAY_STRING_BASE || errorMessage.CURRENCY_DISPLAY_STRING_EMPTY || errorMessage.CURRENCY_DISPLAY_MAXIMUM_LENGTH || errorMessage.CURRENCY_DISPLAY_ANY_REQUIRED}
                  required={true}
                  style={{ margin: '1rem 0' }}
                />
              </Col>
              <Col sm={12}>
                <InputTextField
                  onchange={(event) => {
                    setCurrencyName(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 150 }}
                  value={currencyName}
                  label={t('CURRENCY.INPUT.CURRENCY_NAME')}
                  helperText={
                    errorMessage.CURRENCY_NAME ||
                    errorMessage.IS_DUPLICATE_CURRENCY_NAME ||
                    errorMessage.CURRENCY_NAME_STRING_BASE ||
                    errorMessage.CURRENCY_NAME_STRING_EMPTY ||
                    errorMessage.CURRENCY_NAME_MAXIMUM_LENGTH ||
                    errorMessage.CURRENCY_NAME_ANY_REQUIRED
                  }
                  required={true}
                  style={{ marginBottom: '1rem' }}
                />
              </Col>
              <Col sm={12}>
                <InputTextField
                  onchange={(event) => {
                    setCurrencyNameEn(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 150 }}
                  value={currencyNameEn}
                  label={t('CURRENCY.INPUT.CURRENCY_NAME_EN')}
                  style={{ marginBottom: '1rem' }}
                />
              </Col>
              <Col sm={12}>
                <InputCheckbox
                  checked={currencyFixRatio === 'FIXED' ? true : false}
                  onChange={(event) => {
                    setCurrencyFixRatio(event.target.checked ? 'FIXED' : 'NOT_FIXED')
                  }}
                  className={classes.inputCheckBox}
                  label={t(`CURRENCY.INPUT.CURRENCY_FIX_RATIO`)}
                />
              </Col>
              <Col sm={12}>
                <InputTextField
                  onchange={(event) => {
                    setCurrencyRatio(event.target.value.replace(/[^0-9]/g, ''))
                    clearErrorMessage()
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  value={currencyRatio}
                  label={t('CURRENCY.INPUT.CURRENCY_RATIO')}
                  helperText={errorMessage.CURRENCY_RATIO}
                  required={true}
                  style={{ marginTop: '1rem' }}
                />
              </Col>
            </Row>
          </div>
        }
        onReset={resetForm}
        onSubmit={currencyId ? submitEdit : submit}
        textBtnCancel={t('CURRENCY.BUTTON.CANCEL')}
        textBtnConfirm={currencyId ? t('CURRENCY.BUTTON.SAVE') : t('CURRENCY.BUTTON.UPDATE')}
      />
    </div>
  );
}
